import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import DsModal from '../../common/DsModal';
import { useToast } from '../../common/ToastSystem';
import QuickTaskPanel from './QuickTaskPanel';
import QuickTaskModal from './QuickTaskModal';

const eventDetailPattern = /^\/admin\/workspaces\/event-operations\/events\/([^/?#]+)/;
const artistDetailPattern = /^\/admin\/workspaces\/artist-management\/artists\/([^/?#]+)/;
const ADMIN_EVENT_STATIC_SEGMENTS = new Set(['register', 'registration-complete']);
const isAdminWorkspacePath = (pathname) => {
  const path = String(pathname || '');
  return path.startsWith('/admin/workspaces') || path.startsWith('/admin/settings');
};

const decodePathValue = (value) => {
  try {
    return decodeURIComponent(String(value || '').trim());
  } catch (_error) {
    return String(value || '').trim();
  }
};

const buildSourceFromPath = (pathname) => {
  const path = String(pathname || '');
  const eventMatch = path.match(eventDetailPattern);
  if (eventMatch?.[1] && !ADMIN_EVENT_STATIC_SEGMENTS.has(eventMatch[1])) {
    const eventUuid = decodePathValue(eventMatch[1]);
    return {
      type: 'event',
      eventUuid,
      id: eventUuid,
      scope: 'admin_event_detail',
    };
  }
  const artistMatch = path.match(artistDetailPattern);
  if (artistMatch?.[1]) {
    const artistId = decodePathValue(artistMatch[1]);
    return {
      type: 'artist',
      artistId,
      id: artistId,
      scope: 'admin_artist_detail',
    };
  }
  return {
    type: 'manual',
    scope: 'admin_global_quick_task',
  };
};

export default function AdminQuickTaskGlobalLauncher() {
  const location = useLocation();
  const { success, error: toastError } = useToast();
  const [listOpen, setListOpen] = useState(false);
  const [composerSource, setComposerSource] = useState(null);
  const [reloadSignal, setReloadSignal] = useState(0);

  const currentSource = useMemo(
    () => buildSourceFromPath(location.pathname),
    [location.pathname],
  );

  const isAdminPath = isAdminWorkspacePath(location.pathname);

  const openList = useCallback(() => {
    setListOpen(true);
  }, []);

  const closeList = useCallback(() => {
    setListOpen(false);
  }, []);

  const openComposer = useCallback((source = null) => {
    setComposerSource(source || currentSource);
  }, [currentSource]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleKeyDown = (event) => {
      const isQuickTaskShortcutKey =
        String(event.code || '').toLowerCase() === 'keyt' ||
        String(event.key || '').toLowerCase() === 't';
      if (!(event.metaKey || event.ctrlKey) || !event.altKey || !isQuickTaskShortcutKey) return;
      if (!isAdminWorkspacePath(window.location.pathname)) return;
      event.preventDefault();
      event.stopPropagation();
      openComposer();
    };
    const handleOpenRequest = () => {
      if (!isAdminWorkspacePath(window.location.pathname)) return;
      openList();
    };
    const handleComposeRequest = (event) => {
      if (!isAdminWorkspacePath(window.location.pathname)) return;
      const source = event?.detail?.source && typeof event.detail.source === 'object'
        ? event.detail.source
        : null;
      openComposer(source);
    };
    window.addEventListener('keydown', handleKeyDown, true);
    window.addEventListener('casticart:quick-task-open', handleOpenRequest);
    window.addEventListener('casticart:quick-task-compose', handleComposeRequest);
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
      window.removeEventListener('casticart:quick-task-open', handleOpenRequest);
      window.removeEventListener('casticart:quick-task-compose', handleComposeRequest);
    };
  }, [openComposer, openList]);

  if (!isAdminPath) return null;

  return (
    <>
      {listOpen ? (
        <DsModal
          open={listOpen}
          onClose={closeList}
          title="クイックタスク"
          size="lg"
          className="qt-globalModal"
        >
          <QuickTaskPanel
            filter="mine"
            title="自分に関連するクイックタスク"
            limit={20}
            inlineAdd={false}
            framed={false}
            showHeader={false}
            showFooterLink={false}
            compact
            composerSource={currentSource}
            reloadSignal={reloadSignal}
            onOpenComposer={openComposer}
            onSuccess={success}
            onError={toastError}
          />
        </DsModal>
      ) : null}

      <QuickTaskModal
        open={Boolean(composerSource)}
        source={composerSource}
        onClose={() => setComposerSource(null)}
        onCreated={() => {
          setComposerSource(null);
          setReloadSignal((value) => value + 1);
          success?.('クイックタスクを作成しました。');
        }}
      />
    </>
  );
}
